// useObjects — TanStack Query hook for paginated/searched object instance lookup.
// Used by the instance picker (per ontology spec §10) to power typed Object
// selection in trigger rules and (future) test-context inputs.
//
// Search hits the GIN trigram index on objects.searchable_text via ILIKE.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ObjectInstance {
  id: string;
  object_type_id: string;
  pk_value: string;
  data: Record<string, unknown>;
}

interface UseObjectsArgs {
  /** Object Type id to scope the search. When null, the hook is disabled. */
  objectTypeId: string | null;
  /** Optional ILIKE search term against the searchable_text generated column. */
  search?: string;
  /** Page size. */
  limit?: number;
}

const DEFAULT_LIMIT = 50;

export function useObjects({ objectTypeId, search, limit = DEFAULT_LIMIT }: UseObjectsArgs) {
  const trimmed = (search ?? "").trim();
  const query = useQuery({
    queryKey: ["objects", objectTypeId, trimmed, limit] as const,
    enabled: objectTypeId !== null,
    queryFn: async () => {
      if (!objectTypeId) return [];
      let q = supabase
        .from("objects")
        .select("id, object_type_id, pk_value, data")
        .eq("object_type_id", objectTypeId);

      if (trimmed.length > 0) {
        // GIN trigram index supports ILIKE on the searchable_text column.
        q = q.ilike("searchable_text", `%${trimmed}%`);
      }

      const { data, error } = await q.order("pk_value", { ascending: true }).limit(limit);
      if (error) throw new Error(error.message);
      return (data ?? []) as ObjectInstance[];
    },
  });

  return { objects: query.data ?? [], isLoading: query.isLoading, error: query.error };
}
